import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage } from 'aws-amplify/utils';
import { config, usingCognitoEmulator } from '../config';

/**
 * Configures Amplify against the app's Cognito pool.
 *
 * Amplify rather than the raw `@aws-sdk/client-cognito-identity-provider`,
 * because Amplify owns the two things worth not writing: the SRP handshake
 * and token refresh, so an expired access token is refreshed rather than
 * quietly 401ing every request while the app still believes the session is
 * valid.
 */
export const configureAuth = (): void => {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config().cognito.userPoolId,
        userPoolClientId: config().cognito.clientId,
        // Points Amplify at cognito-local. Absent in production, where it
        // derives the real AWS endpoint from the pool's region.
        ...(config().cognito.endpoint ? { userPoolEndpoint: config().cognito.endpoint } : {}),
      },
    },
  });

  // localStorage, not the default in-memory-plus-cookies arrangement: tokens
  // must survive a page reload, and this is an internal tool on office
  // workstations.
  cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
};

/**
 * Which Cognito flow to authenticate with.
 *
 * SRP proves the password without sending it, and real Cognito supports it.
 * `cognito-local` answers *"unsupported feature: InitAuth with
 * AuthFlow=USER_SRP_AUTH"*, so against an emulator — and only against an
 * emulator — this falls back to `USER_PASSWORD_AUTH`.
 *
 * Everything downstream of the handshake is identical either way: same tokens,
 * same claims, same guard. Production authentication is unchanged.
 */
export const authFlowType = (): 'USER_SRP_AUTH' | 'USER_PASSWORD_AUTH' =>
  usingCognitoEmulator() ? 'USER_PASSWORD_AUTH' : 'USER_SRP_AUTH';
